import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Fade,
  Grow,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  Today as TodayIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { styled } from '@mui/material/styles';
import { useUser } from '../../context/UserContext';
import { theme, responsiveSpacing, responsiveFontSizes } from '../../theme/theme';
import { generateFortuneReport } from '../../services/deepseekApi';
import { astro } from 'iztro';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { processChineseText, containsChinese } from '../../utils/pdfFonts';
import { setupPDFFont } from '../../utils/sourceHanSansFont';
import { registerDemoFont, smartTextProcess } from '../../utils/demoFont';
import { SubscriptionGate, usePermissionCheck } from '../../components/SubscriptionGate';
import { ProductType } from '../../types/subscription';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

const ReportCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

const IconBox = styled(Box)<{ color: string }>(({ color, theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 32,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: `${color}20`,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
}));

interface ReportType {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<SvgIconProps>;
  color: string;
  scope: 'decadal' | 'yearly' | 'monthly' | 'daily';
}

const ReportsScreen = () => {
  const { birthInfo, astrolabe } = useUser();
  const [loading, setLoading] = useState<string | null>(null);
  const [reports, setReports] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<string | null>(null);

  const reportTypes: ReportType[] = [
    {
      id: 'decadal',
      title: '十年运势报告',
      description: '深入分析您未来十年的运势走向',
      icon: TrendingUpIcon,
      color: '#9C27B0',
      scope: 'decadal',
    },
    {
      id: 'yearly',
      title: '年度运势报告',
      description: '详细解读您今年的整体运势',
      icon: CalendarTodayIcon,
      color: '#2196F3',
      scope: 'yearly',
    },
    {
      id: 'monthly',
      title: '月度运势报告',
      description: '分析您本月的运势变化',
      icon: DateRangeIcon,
      color: '#FF9800',
      scope: 'monthly',
    },
    {
      id: 'daily',
      title: '每日运势报告',
      description: '了解您今日的运势指导',
      icon: TodayIcon,
      color: '#4CAF50',
      scope: 'daily',
    },
  ];

  const generateReport = async (reportType: ReportType) => {
    if (!birthInfo || !astrolabe) {
      setError('需要出生信息才能生成报告');
      return;
    }

    setLoading(reportType.id);
    setError(null);

    try {
      // Get current date for horoscope calculation
      const currentDate = new Date();

      // 使用 DeepSeek 的报告生成（替换 VertexAI）
      const report = await generateFortuneReport({
        birthInfo,
        astrolabe: astrolabe as any,
        scope: reportType.scope as 'decadal' | 'yearly' | 'monthly' | 'daily',
        reportType: reportType.title,
        horoscopeDate: currentDate,
        scopeData: {},
        language: 'zh-CN',
      });

      setReports(prev => ({ ...prev, [reportType.id]: report }));
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const downloadReportAsPDF = (reportType: ReportType, reportContent: string) => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Try to register demo font
       const demoFontRegistered = registerDemoFont(doc);
       
       // Set up PDF styling with better font handling
       const fontAvailable = setupPDFFont(doc);
       
       // Determine processing method based on font availability
       const shouldProcessChinese = !fontAvailable;
       
       // If SourceHanSans is not available, use helvetica as fallback
      if (!fontAvailable) {
        doc.setFont('helvetica', 'normal');
      }
      
      // Character mapping is handled by smartTextProcess function
      console.log('Using enhanced character mapping for Chinese text processing');
      
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      
      // Add title with proper font handling
      const title = reportType.title || 'Fortune Report';
      const processedTitle = fontAvailable ? title : smartTextProcess(title);
      doc.text(processedTitle, 20, 30);
      
      // Add subtitle with birth info
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      
      // Validate birth date values
      const year = birthInfo!.year || new Date().getFullYear();
      const month = Math.max(1, Math.min(12, birthInfo!.month || 1)) - 1; // Convert to 0-based month
      const day = Math.max(1, Math.min(31, birthInfo!.day || 1));
      
      const birthDate = new Date(year, month, day);
      
      // Check if the date is valid
      const birthDateStr = isNaN(birthDate.getTime()) 
        ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        : birthDate.toLocaleDateString();
      
      const subtitle = `Generated for: ${birthDateStr} | ${new Date().toLocaleDateString()}`;
      doc.text(subtitle, 20, 45);
      
      // Add content with better text handling
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      
      // Clean and process content for better PDF display
      const cleanContent = fontAvailable ? reportContent : smartTextProcess(reportContent);
      
      // Split content into lines that fit the page width
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxLineWidth = pageWidth - (margin * 2);
      
      // Use cleaned content for better display
      const lines = doc.splitTextToSize(cleanContent, maxLineWidth);
      
      let yPosition = 60;
      const lineHeight = 6; // Reduced line height for better spacing
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Set character spacing for better readability
      doc.setCharSpace(0.1);
      
      lines.forEach((line: string) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 30;
        }
        // Trim whitespace and ensure proper spacing
        const trimmedLine = line.trim();
        if (trimmedLine) {
          doc.text(trimmedLine, margin, yPosition);
        }
        yPosition += lineHeight;
      });
      
      // Add footer
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
        `紫微AI - 第 ${i} 页 / 共 ${totalPages} 页`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }
      
      // Generate filename
      const dateStr = isNaN(birthDate.getTime()) 
        ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        : birthDate.toISOString().split('T')[0];
      const filename = `${reportType.title.replace(/\s+/g, '_')}_${dateStr}.pdf`;
      
      // Download the PDF
      doc.save(filename);
      
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    }
  };

  if (!birthInfo) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        backgroundColor: theme.colors.background,
        padding: responsiveSpacing.component,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            需要出生信息才能生成报告
          </Typography>
          <Typography>
            请完善您的出生信息以生成运势报告
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.colors.background,
      paddingBottom: { xs: '80px', sm: '80px' }
    }}>
      {/* Header */}
      <Box sx={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
        padding: responsiveSpacing.component,
        color: 'white'
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold',
            fontSize: responsiveFontSizes.h3,
            textAlign: 'center'
          }}
        >
          运势报告
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            opacity: 0.9,
            textAlign: 'center',
            mt: 1,
            fontSize: responsiveFontSizes.body1
          }}
        >
          全面的运势分析报告
        </Typography>
      </Box>

      <Box sx={{ padding: responsiveSpacing.component }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {/* Report Types Grid */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
          {reportTypes.map((reportType, index) => {
            const IconComponent = reportType.icon;
            const hasReport = reports[reportType.id];
            const isLoading = loading === reportType.id;

            const getProductType = (reportId: string): ProductType => {
              switch (reportId) {
                case 'yearly': return 'reports';
                case 'monthly': return 'reports';
                case 'daily': return 'reports';
                default: return 'reports';
              }
            };

            // decadal 报告永远免费，不需要订阅验证
            const reportContent = reportType.id === 'decadal' ? (
              <ReportCard
                onClick={() => !isLoading && generateReport(reportType)}
                sx={{
                  opacity: isLoading ? 0.7 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              >
                <Box>
                  <IconBox color={reportType.color}>
                    {isLoading ? (
                      <CircularProgress size={32} sx={{ color: reportType.color }} />
                    ) : (
                      <IconComponent sx={{ fontSize: 32, color: reportType.color }} />
                    )}
                  </IconBox>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: theme.colors.text,
                      mb: 1,
                      fontSize: { xs: '1rem', sm: responsiveFontSizes.h6 }
                    }}
                  >
                    {reportType.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.colors.placeholder,
                      fontSize: { xs: '0.8rem', sm: responsiveFontSizes.body2 },
                      lineHeight: 1.4
                    }}
                  >
                    {reportType.description}
                  </Typography>
                </Box>
                <Button
                  variant={hasReport ? "outlined" : "contained"}
                  disabled={isLoading}
                  onClick={hasReport ? (e) => {
                    e.stopPropagation();
                    downloadReportAsPDF(reportType, reports[reportType.id]);
                  } : undefined}
                  startIcon={hasReport ? <DownloadIcon /> : undefined}
                  sx={{
                    mt: 2,
                    borderRadius: 20,
                    backgroundColor: hasReport ? 'transparent' : reportType.color,
                    borderColor: reportType.color,
                    color: hasReport ? reportType.color : 'white',
                    minHeight: { xs: 40, sm: 36 },
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 2, sm: 3 },
                    '&:hover': {
                      backgroundColor: hasReport ? `${reportType.color}10` : `${reportType.color}dd`,
                    },
                  }}
                >
                  {isLoading ? '生成中...' : hasReport ? '下载PDF' : '生成报告'}
                </Button>
              </ReportCard>
            ) : (
              <SubscriptionGate
                requiredProduct={getProductType(reportType.id)}
                requiredFeature={reportType.scope === 'yearly' ? 'yearlyReport' : 
                        reportType.scope === 'monthly' ? 'monthlyReport' : 
                        reportType.scope === 'daily' ? 'dailyFortune' : 'yearlyReport'}
                fallback={
                  <ReportCard
                    sx={{
                      opacity: 0.6,
                      cursor: 'not-allowed',
                    }}
                  >
                    <Box>
                      <IconBox color={reportType.color}>
                        <IconComponent sx={{ fontSize: 32, color: reportType.color }} />
                      </IconBox>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 'bold',
                          color: theme.colors.text,
                          mb: 1,
                          fontSize: { xs: '1rem', sm: responsiveFontSizes.h6 }
                        }}
                      >
                        {reportType.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.colors.placeholder,
                          fontSize: { xs: '0.8rem', sm: responsiveFontSizes.body2 },
                          lineHeight: 1.4
                        }}
                      >
                        {reportType.description}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      disabled
                      sx={{
                        mt: 2,
                        borderRadius: 20,
                        backgroundColor: theme.colors.placeholder,
                        minHeight: { xs: 40, sm: 36 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        px: { xs: 2, sm: 3 },
                      }}
                    >
                      需要升级
                    </Button>
                  </ReportCard>
                }
              >
                <ReportCard
                  onClick={() => !isLoading && generateReport(reportType)}
                  sx={{
                    opacity: isLoading ? 0.7 : 1,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Box>
                    <IconBox color={reportType.color}>
                      {isLoading ? (
                        <CircularProgress size={32} sx={{ color: reportType.color }} />
                      ) : (
                        <IconComponent sx={{ fontSize: 32, color: reportType.color }} />
                      )}
                    </IconBox>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: theme.colors.text,
                        mb: 1,
                        fontSize: { xs: '1rem', sm: responsiveFontSizes.h6 }
                      }}
                    >
                      {reportType.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.colors.placeholder,
                        fontSize: { xs: '0.8rem', sm: responsiveFontSizes.body2 },
                        lineHeight: 1.4
                      }}
                    >
                      {reportType.description}
                    </Typography>
                  </Box>
                  <Button
                    variant={hasReport ? "outlined" : "contained"}
                    disabled={isLoading}
                    onClick={hasReport ? (e) => {
                      e.stopPropagation();
                      downloadReportAsPDF(reportType, reports[reportType.id]);
                    } : undefined}
                    startIcon={hasReport ? <DownloadIcon /> : undefined}
                    sx={{
                      mt: 2,
                      borderRadius: 20,
                      backgroundColor: hasReport ? 'transparent' : reportType.color,
                      borderColor: reportType.color,
                      color: hasReport ? reportType.color : 'white',
                      minHeight: { xs: 40, sm: 36 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 3 },
                      '&:hover': {
                        backgroundColor: hasReport ? `${reportType.color}10` : `${reportType.color}dd`,
                      },
                    }}
                  >
                    {isLoading ? '生成中...' : hasReport ? '下载PDF' : '生成报告'}
                  </Button>
                </ReportCard>
              </SubscriptionGate>
            );

            return (
              <Grid item xs={12} sm={6} lg={3} key={reportType.id}>
                <Grow in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  {reportContent}
                </Grow>
              </Grid>
            );
          })}
        </Grid>

        {/* Generated Reports */}
        {Object.entries(reports).map(([reportId, reportContent]) => {
          const reportType = reportTypes.find(r => r.id === reportId);
          if (!reportType) return null;

          return (
            <Fade in timeout={800} key={reportId}>
              <StyledCard sx={{ mb: { xs: 2, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    textAlign: { xs: 'center', sm: 'left' }
                  }}>
                    <IconBox color={reportType.color} sx={{ 
                      width: { xs: 40, sm: 48 }, 
                      height: { xs: 40, sm: 48 }, 
                      mr: { xs: 0, sm: 2 }, 
                      mb: { xs: 1, sm: 0 } 
                    }}>
                      {React.createElement(reportType.icon, { sx: { fontSize: 24, color: reportType.color } })}
                    </IconBox>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: theme.colors.primary,
                        fontSize: responsiveFontSizes.h5
                      }}
                    >
                      {reportType.title}
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.colors.text,
                      lineHeight: { xs: 1.6, sm: 1.8 },
                      fontSize: { xs: '0.9rem', sm: responsiveFontSizes.body1 },
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {reportContent}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Fade>
          );
        })}
      </Box>
    </Box>
  );
};

export default ReportsScreen;